import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import {
  filterEntityUnitTeamOptions,
  filterUnitTeamOptions,
  type UnitTeamEntityOption,
  type UnitTeamOption,
} from "./unitTeamDropdown.logic";
import { UnitTeamDropdownOptions } from "./unitTeamDropdown.options.component";
import {
  ActionSlot,
  ClearButton,
  Container,
  DropdownState,
  EmptyState,
  FieldWrapper,
  IconWrapper,
  Input,
  Label,
  List,
  ListScroll,
  SelectionBadge,
  TooltipAligner,
  TooltipColumn,
  TooltipItem,
  TooltipList,
} from "./unitTeamDropdown.styles";

export type { UnitTeamOption } from "./unitTeamDropdown.logic";

interface UnitTeamDropdownProps {
  readonly emptyText?: string;
  readonly entityOptions?: readonly UnitTeamEntityOption[];
  readonly label?: string;
  readonly options: readonly UnitTeamOption[];
  readonly placeholder: string;
  readonly selectedEntityId?: number | null;
  readonly selectedTeamIds: readonly string[];
  readonly selectedUnitId?: number | null;
  readonly onSelectedEntityIdChange?: (value: number) => void;
  readonly onSelectedTeamIdsChange: (values: string[]) => void;
  readonly onSelectedUnitIdChange?: (value: number | null) => void;
}

interface SelectedLabel {
  readonly displayLabel: string;
  readonly extraCount: number;
  readonly labels: string[];
}

interface BuildSelectedLabelParams {
  readonly options: readonly UnitTeamOption[];
  readonly selectedTeamIds: readonly string[];
  readonly selectedUnitId?: number | null;
  readonly translate: (
    key: string,
    options?: { defaultValue?: string },
  ) => string;
}

const buildSelectedLabel = ({
  options,
  selectedTeamIds,
  selectedUnitId,
  translate,
}: BuildSelectedLabelParams): SelectedLabel => {
  if (selectedUnitId != null) {
    const selectedUnit = options.find((unit) => unit.unitId === selectedUnitId);
    if (selectedUnit) {
      const entityName = (selectedUnit.entityName ?? "").trim();
      const unitName =
        (selectedUnit.unitName ?? "").trim() ||
        translate("Schedule.UnknownUnit");
      const label = entityName ? `${entityName}/${unitName}` : unitName;
      return { displayLabel: label, extraCount: 0, labels: [label] };
    }
  }
  if (selectedTeamIds.length === 0) {
    return { displayLabel: "", extraCount: 0, labels: [] };
  }
  const selectedSet = new Set(selectedTeamIds);
  const labels: string[] = [];
  options.forEach((unit) => {
    const entityName = (unit.entityName ?? "").trim();
    const unitName =
      (unit.unitName ?? "").trim() || translate("Schedule.UnknownUnit");
    unit.teams.forEach((team) => {
      if (selectedSet.has(String(team.id))) {
        const teamName =
          (team.name ?? "").trim() || translate("Schedule.UnknownTeam");
        labels.push(
          entityName
            ? `${entityName}/${unitName}/${teamName}`
            : `${unitName}/${teamName}`,
        );
      }
    });
  });
  const displayLabels = labels.slice(0, 2);
  const extraCount = Math.max(labels.length - displayLabels.length, 0);
  return {
    displayLabel: displayLabels.join(", "),
    extraCount,
    labels,
  };
};

const splitLabels = (labels: string[]): [string[], string[]] => {
  if (labels.length <= 6) return [labels, []];
  const mid = Math.ceil(labels.length / 2);
  return [labels.slice(0, mid), labels.slice(mid)];
};

export const UnitTeamDropdown: React.FC<UnitTeamDropdownProps> = (props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLabel = useMemo(
    () =>
      buildSelectedLabel({
        options: props.options,
        selectedTeamIds: props.selectedTeamIds,
        selectedUnitId: props.selectedUnitId,
        translate: t,
      }),
    [props.options, props.selectedTeamIds, props.selectedUnitId, t],
  );
  const filteredOptions = useMemo(
    () =>
      filterUnitTeamOptions({
        options: props.options,
        query,
        unknownTeamLabel: t("Schedule.UnknownTeam"),
        unknownUnitLabel: t("Schedule.UnknownUnit"),
      }),
    [props.options, query, t],
  );

  useOnClickOutside(
    [containerRef],
    () => {
      setIsOpen(false);
      setQuery(selectedLabel.displayLabel);
    },
    { enabled: isOpen, eventType: "mousedown" },
  );

  const selectedValueSet = useMemo(
    () => new Set(props.selectedTeamIds),
    [props.selectedTeamIds],
  );
  const groupedOptions = useMemo(
    () =>
      filterEntityUnitTeamOptions({
        entities: props.entityOptions ?? [],
        options: props.options,
        query,
        unknownTeamLabel: t("Schedule.UnknownTeam"),
        unknownUnitLabel: t("Schedule.UnknownUnit"),
      }),
    [props.entityOptions, props.options, query, t],
  );
  const showEntityLevel = props.onSelectedEntityIdChange != null;

  const visualState: DropdownState = useMemo(() => {
    if (isOpen) return "active";
    if (props.selectedTeamIds.length > 0 || props.selectedUnitId != null) {
      return "filled";
    }
    return "inactive";
  }, [isOpen, props.selectedTeamIds.length, props.selectedUnitId]);
  const canClear =
    props.selectedTeamIds.length > 0 || props.selectedUnitId != null;

  const openDropdown = (): void => {
    setIsOpen(true);
    setQuery("");
  };

  const closeDropdown = (): void => {
    setIsOpen(false);
    setQuery(selectedLabel.displayLabel);
  };

  const toggleTeam = (teamId: number): void => {
    const next = new Set(selectedValueSet);
    const value = String(teamId);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    props.onSelectedTeamIdsChange(Array.from(next));
  };

  const toggleUnit = (unit: UnitTeamOption): void => {
    if (props.onSelectedUnitIdChange) {
      props.onSelectedUnitIdChange(
        props.selectedUnitId === unit.unitId ? null : unit.unitId,
      );
      return;
    }
    const next = new Set(selectedValueSet);
    const unitTeamIds = unit.teams.map((team) => String(team.id));
    const selectedCount = unitTeamIds.filter((id) => next.has(id)).length;
    const shouldSelectAll = selectedCount !== unitTeamIds.length;
    unitTeamIds.forEach((id) => {
      if (shouldSelectAll) next.add(id);
      else next.delete(id);
    });
    props.onSelectedTeamIdsChange(Array.from(next));
  };

  const selectEntity = (entityId: number): void => {
    props.onSelectedEntityIdChange?.(entityId);
  };

  const resolvedValue = isOpen ? query : selectedLabel.displayLabel;
  const placeholderText = props.placeholder;
  const emptyText =
    props.emptyText ?? t("OrganizationalStructure.NoUnitTeamMatches");
  const showExtraCountBadge = !isOpen && selectedLabel.extraCount > 0;

  return (
    <Container ref={containerRef}>
      {props.label && <Label>{props.label}</Label>}
      <FieldWrapper
        $state={visualState}
        onClick={() => {
          if (!isOpen) openDropdown();
        }}
      >
        <Input
          type="text"
          value={resolvedValue}
          placeholder={placeholderText}
          onChange={(event) => {
            if (!isOpen) setIsOpen(true);
            setQuery(event.target.value);
          }}
          onFocus={openDropdown}
        />
        {showExtraCountBadge && (
          <TooltipAligner>
            <TooltipHover
              content={
                <TooltipList $columns={selectedLabel.labels.length > 6 ? 2 : 1}>
                  {splitLabels(selectedLabel.labels).map(
                    (column, columnIndex) => (
                      <TooltipColumn key={`column-${columnIndex}`}>
                        {column.map((label) => (
                          <TooltipItem key={label}>{label}</TooltipItem>
                        ))}
                      </TooltipColumn>
                    ),
                  )}
                </TooltipList>
              }
              arrow="down"
              textAlign="left"
              minWidth={230}
              maxWidth={330}
            >
              <SelectionBadge>+{selectedLabel.extraCount}</SelectionBadge>
            </TooltipHover>
          </TooltipAligner>
        )}
        <ActionSlot>
          {canClear && (
            <ClearButton
              type="button"
              aria-label="Clear selection"
              onClick={(event) => {
                event.stopPropagation();
                props.onSelectedTeamIdsChange([]);
                props.onSelectedUnitIdChange?.(null);
                setQuery("");
              }}
            >
              <CustomIcon name="v2/cross" size="16px" color={ColorV2.Blue5} />
            </ClearButton>
          )}
          <IconWrapper
            type="button"
            aria-label="Toggle dropdown"
            onClick={(event) => {
              event.stopPropagation();
              if (isOpen) closeDropdown();
              else openDropdown();
            }}
          >
            <CustomIcon
              name={isOpen ? "v2/up-chevron" : "v2/down-chevron"}
              size="20px"
              color={ColorV2.Blue5}
            />
          </IconWrapper>
        </ActionSlot>
      </FieldWrapper>
      <List $isOpen={isOpen}>
        <ListScroll>
          {(showEntityLevel
            ? groupedOptions.length === 0
            : filteredOptions.length === 0) && (
            <EmptyState>{emptyText}</EmptyState>
          )}
          <UnitTeamDropdownOptions
            entities={groupedOptions}
            selectedEntityId={props.selectedEntityId}
            selectedTeamIds={selectedValueSet}
            selectedUnitId={props.selectedUnitId}
            showEntityLevel={showEntityLevel}
            units={filteredOptions}
            unknownTeamLabel={t("Schedule.UnknownTeam")}
            unknownUnitLabel={t("Schedule.UnknownUnit")}
            onEntitySelect={selectEntity}
            onTeamToggle={toggleTeam}
            onUnitToggle={toggleUnit}
          />
        </ListScroll>
      </List>
    </Container>
  );
};
